import { useThreadMessages } from "@convex-dev/agent/react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { api } from "../../../convex/_generated/api";
import { MessageEntry, type MessageEntryProps } from "./MessageEntry";
import {
	buildToolResultMap,
	type ChatMessageLike,
	groupMessagesByTurn,
	type TurnGroup,
} from "./messageHelpers";
import type { ThreadChild } from "./types";

/**
 * Builds the message shape MessageEntry expects from a TurnGroup: reuses the
 * last row's metadata (_id, order, stepOrder, status, tool) but swaps in the
 * merged content parts + running text.
 */
function synthesizeMessage(turn: TurnGroup): MessageEntryProps["message"] {
	const last = turn.messages[turn.messages.length - 1];
	return {
		_id: last._id,
		order: last.order ?? 0,
		stepOrder: last.stepOrder ?? 0,
		status: last.status ?? "",
		tool: last.message?.role === "tool",
		agentName: undefined,
		streaming: turn.streaming,
		text: turn.text,
		message: {
			role: turn.role,
			content: [...turn.parts],
		},
	};
}

export function MessageTimeline({
	clericalThreadId,
	childThreads,
}: {
	clericalThreadId: string;
	childThreads: Map<string, ThreadChild>;
}) {
	const { results, status, loadMore } = useThreadMessages(
		api.agent.queries.listThreadMessages,
		{ threadId: clericalThreadId },
		{ initialNumItems: 50, stream: true },
	);

	const toolResults = useMemo(() => buildToolResultMap(results), [results]);

	// Same grouping as chat UI: collapse same-`order` rows into one card;
	// tool messages drop from the list but still feed buildToolResultMap.
	const turns = useMemo(
		() => groupMessagesByTurn(results as unknown as ChatMessageLike[]),
		[results],
	);

	if (status === "LoadingFirstPage") {
		return (
			<p className="py-4 text-center text-sm text-muted-foreground">
				Loading messages...
			</p>
		);
	}

	if (turns.length === 0) {
		return (
			<p className="py-4 text-center text-sm text-muted-foreground">
				No messages yet.
			</p>
		);
	}

	return (
		<div className="space-y-3">
			{status === "CanLoadMore" && (
				<Button
					variant="ghost"
					size="sm"
					onClick={() => loadMore(50)}
					className="w-full bg-muted text-xs text-muted-foreground hover:bg-muted/80"
				>
					Load older messages
				</Button>
			)}
			{turns.map((turn) => (
				<MessageEntry
					key={turn.key}
					message={synthesizeMessage(turn)}
					toolResults={toolResults}
					childThreads={childThreads}
				/>
			))}
		</div>
	);
}
